import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  FileText,
  Image,
  Table,
  Share,
  Settings,
  CheckCircle,
  Clock,
  ArrowRight,
  Eye,
  Calendar,
  Users,
  Layers,
  BarChart3,
  Mail,
  Link,
  Printer
} from 'lucide-react';
import { demoRoadmap } from '../../data/demoData';

const ExportHub = () => {
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('modern');
  const [exportOptions, setExportOptions] = useState({
    includeTimeline: true,
    includeZachman: true,
    includeTRIZ: true,
    includeFeatures: true,
    includeMilestones: true,
    includeAnalytics: false
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  const formats = [
    {
      id: 'pdf',
      name: 'PDF Document',
      icon: FileText,
      description: 'Professional PDF report with charts and tables',
      size: '2-5 MB',
      color: 'from-red-500 to-red-600'
    },
    {
      id: 'excel',
      name: 'Excel Spreadsheet',
      icon: Table,
      description: 'Interactive spreadsheet with data analysis',
      size: '1-3 MB',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'png',
      name: 'PNG Image',
      icon: Image,
      description: 'High-resolution visual roadmap image',
      size: '500KB - 2MB',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'json',
      name: 'JSON Data',
      icon: Settings,
      description: 'Structured data for API integration',
      size: '50-200KB',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const templates = [
    {
      id: 'modern',
      name: 'Modern Executive',
      description: 'Clean, contemporary design for C-level presentations',
      preview: '/api/placeholder/300/200'
    },
    {
      id: 'classic',
      name: 'Classic Corporate',
      description: 'Traditional corporate format with formal styling',
      preview: '/api/placeholder/300/200'
    },
    {
      id: 'technical',
      name: 'Technical Deep-Dive',
      description: 'Detailed technical documentation with architecture focus',
      preview: '/api/placeholder/300/200'
    }
  ];

  const shareOptions = [
    {
      id: 'email',
      name: 'Email',
      icon: Mail,
      description: 'Send via email with custom message'
    },
    {
      id: 'link',
      name: 'Share Link',
      icon: Link,
      description: 'Generate shareable link with access control'
    },
    {
      id: 'print',
      name: 'Print',
      icon: Printer,
      description: 'Direct print with optimized formatting'
    }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    setIsExporting(false);
    setExportComplete(true);
    
    // Reset after 3 seconds
    setTimeout(() => setExportComplete(false), 3000);
  };

  const handleOptionChange = (option: keyof typeof exportOptions) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto h-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold gradient-text mb-2">Export & Share Hub</h1>
              <p className="text-slate-600 dark:text-slate-400">
                Export your roadmap in multiple formats and share with stakeholders
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-lg p-3 border border-slate-200/50 dark:border-slate-700/50">
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {demoRoadmap.name}
                  </span>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
          <div className="xl:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6"
            >
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
                Export Format
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formats.map((format, index) => {
                  const Icon = format.icon;
                  
                  return (
                    <motion.div
                      key={format.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedFormat(format.id)}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                        selectedFormat === format.id
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
                          : 'bg-slate-50 dark:bg-slate-800 border-2 border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-12 h-12 bg-gradient-to-r ${format.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                            {format.name}
                          </h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                            {format.description}
                          </p>
                          <div className="text-xs text-slate-500 dark:text-slate-500">
                            Est. size: {format.size}
                          </div>
                        </div>
                        
                        {selectedFormat === format.id && (
                          <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6"
            >
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
                Template Selection
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {templates.map((template, index) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`cursor-pointer transition-all duration-200 ${
                      selectedTemplate === template.id
                        ? 'ring-2 ring-blue-500'
                        : 'hover:ring-2 hover:ring-slate-300 dark:hover:ring-slate-600'
                    }`}
                  >
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg aspect-video mb-3 flex items-center justify-center">
                      <Eye className="w-8 h-8 text-slate-400" />
                    </div>
                    <div className="px-2">
                      <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1">
                        {template.name}
                      </h4>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        {template.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6"
            >
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
                Content Options
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(exportOptions).map(([key, value]) => {
                  const icons = {
                    includeTimeline: Calendar,
                    includeZachman: Layers,
                    includeTRIZ: Settings,
                    includeFeatures: BarChart3,
                    includeMilestones: CheckCircle,
                    includeAnalytics: BarChart3
                  };
                  
                  const labels = {
                    includeTimeline: 'Timeline & Gantt Charts',
                    includeZachman: 'Zachman Architecture',
                    includeTRIZ: 'TRIZ Analysis',
                    includeFeatures: 'Feature Specifications',
                    includeMilestones: 'Milestones & Goals',
                    includeAnalytics: 'Advanced Analytics'
                  };
                  
                  const Icon = icons[key as keyof typeof icons];
                  
                  return (
                    <motion.div
                      key={key}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => handleOptionChange(key as keyof typeof exportOptions)}
                      className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        value 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="font-medium text-slate-900 dark:text-slate-100">
                          {labels[key as keyof typeof labels]}
                        </div>
                      </div>
                      
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        value 
                          ? 'border-blue-500 bg-blue-500' 
                          : 'border-slate-300 dark:border-slate-600'
                      }`}>
                        {value && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Export Preview
              </h3>
              
              <div className="space-y-4">
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg aspect-video flex items-center justify-center">
                  <div className="text-center">
                    <Eye className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Preview will appear here
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Format:</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100 capitalize">
                      {selectedFormat}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Template:</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {templates.find(t => t.id === selectedTemplate)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Sections:</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {Object.values(exportOptions).filter(Boolean).length}/6
                    </span>
                  </div>
                </div>
              </div>
              
              <AnimatePresence mode="wait">
                {!isExporting && !exportComplete ? (
                  <motion.button
                    key="export-button"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleExport}
                    className="btn-primary w-full mt-6 flex items-center justify-center space-x-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>Generate Export</span>
                  </motion.button>
                ) : isExporting ? (
                  <motion.div
                    key="exporting"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center space-x-3 mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Clock className="w-5 h-5 text-blue-600" />
                    </motion.div>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">
                      Generating export...
                    </span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="complete"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center space-x-3 mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-700 dark:text-green-300 font-medium">
                      Export ready for download!
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Share Options
              </h3>
              
              <div className="space-y-3">
                {shareOptions.map((option, index) => {
                  const Icon = option.icon;
                  
                  return (
                    <motion.button
                      key={option.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      
                      <div className="flex-1 text-left">
                        <div className="font-medium text-slate-900 dark:text-slate-100">
                          {option.name}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                          {option.description}
                        </div>
                      </div>
                      
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl border border-purple-200/50 dark:border-purple-700/50 p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                    Collaboration Features
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Advanced sharing and collaboration tools
                  </p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Real-time comments</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Version control</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Access permissions</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-slate-700 dark:text-slate-300">Stakeholder notifications</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportHub;